import React, { useEffect, useState } from 'react';
import { showToast } from '@deeptrue-frontend/ui';
import queryString from 'query-string';
import { RouteComponentProps } from 'react-router-dom';
import { Dashboard } from 'components/Layout';
import AdminMenu from 'components/AdminMenu/AdminMenu';
import api from 'api/api';
import StoreProvider from 'components/StoreProvider/StoreProvider';
import { PaginationType } from 'api/types';
import EmptyPlaceholder from 'components/EmptyPlaceholder/EmptyPlaceholder';
import AssetsList from 'components/Assets/AssetsList';
import { AssetType } from 'store/modules/assets/assets.types';
import ROUTES from 'consts/routing';
import { useFormatMessage } from 'components/IntlProvider';

const { connect, selectors, actions } = StoreProvider;

interface Props extends RouteComponentProps {
  assets: PaginationType<AssetType>;
  setAssets: (payload: any) => void;
}

const Assets = ({ history, location, setAssets, assets }: Props) => {
  const [isLoading, setIsLoading] = useState(false);
  const parsedSearch = queryString.parse(location.search);
  const t = useFormatMessage();

  useEffect(() => {
    if (parsedSearch.action === 'asset_deleted') {
      showToast({
        type: 'info',
        title: t('dashboard.assets.deleted', 'Asset deleted'),
      });
    }
    if (parsedSearch.action === 'asset_updated') {
      showToast({
        type: 'info',
        title: t('dashboard.assets.updated', 'Asset updated'),
      });
    }
    if (parsedSearch.action === 'asset_added') {
      showToast({
        type: 'info',
        title: t('dashboard.assets.added', 'Asset added'),
      });
    }

    setIsLoading(true);

    api.resources
      .getAssets(
        parsedSearch as {
          limit: string;
          offset: string;
        }
      )
      .then(({ data }) => {
        setAssets(data);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, location.key]);

  const getPageNumber = () => {
    if (parsedSearch.offset && !Array.isArray(parsedSearch.offset)) {
      return parseInt(parsedSearch.offset, 10) / 20 + 1;
    }

    return 0;
  };

  const removeAsset = (uuid: string) => {
    api.resources
      .removeAssets({ uuid })
      .then(() => {
        const redirectUrl =
          parsedSearch.offset && !Array.isArray(parsedSearch.offset)
            ? `${ROUTES.ADMIN.ASSETS.LIST}?limit=20&offset=${parsedSearch.offset}&action=asset_deleted`
            : `${ROUTES.ADMIN.ASSETS.LIST}?action=asset_deleted`;

        history.push(`${redirectUrl}`);
      })
      .catch(() => {
        showToast({
          type: 'error',
          title: t(
            'errors.toast.general',
            'Something went wrong, please contact administrator'
          ),
        });
      });
  };

  return (
    <Dashboard title="Assets" location={location}>
      <AdminMenu />

      <EmptyPlaceholder
        isLoaded={isLoading}
        hasValues={assets.results.length > 0}
        withLoader
      >
        <AssetsList
          assetList={assets.results}
          count={assets.count}
          currentPage={getPageNumber()}
          removeAsset={removeAsset}
        />
      </EmptyPlaceholder>
    </Dashboard>
  );
};

export default connect(
  (state) => ({
    assets: selectors.assets.list(state),
  }),
  {
    setAssets: actions.assets.setAssets,
  }
)(Assets);
